import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/customer-ui/button'
import { toast } from '@/components/ui/use-toast'
import zeresLogo from '@/assets/logo.webp'
import { useCustomerAccountAuthentication } from '../../use-customer-account-authentication'
import { registerLoginStorageKey } from './page-register'

type RegisterLoginState = {
  email?: string
  password?: string
}

function getStoredLoginState(): RegisterLoginState {
  const value = window.sessionStorage.getItem(registerLoginStorageKey)
  if (value == null) {
    return {}
  }

  try {
    const parsed = JSON.parse(value) as RegisterLoginState

    return {
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      password:
        typeof parsed.password === 'string' ? parsed.password : undefined
    }
  } catch {
    return {}
  }
}

export default function RegisterSuccessful() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { performLogin } = useCustomerAccountAuthentication({
    shouldLoadAccount: false
  })
  const loginState = useMemo<RegisterLoginState>(() => {
    const state = location.state as RegisterLoginState | null

    return {
      ...getStoredLoginState(),
      ...(state ?? {})
    }
  }, [location.state])

  const handlePortalClick = async () => {
    if (!loginState.email || !loginState.password) {
      void navigate('/customer/login')
      return
    }

    setIsSubmitting(true)

    try {
      await performLogin({
        email: loginState.email,
        password: loginState.password
      })
      window.sessionStorage.removeItem(registerLoginStorageKey)
    } catch {
      toast({
        title: 'Inloggen mislukt. Log in met uw e-mailadres en wachtwoord.',
        variant: 'destructive'
      })
      void navigate('/customer/login')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className='bg-background flex min-h-screen items-start justify-center px-6 py-20 sm:py-24'>
      <div className='w-full max-w-md'>
        <div className='mb-8 flex justify-center'>
          <img
            src={zeresLogo}
            alt='Zeres - Zero Emission Services'
            className='h-16 w-auto'
          />
        </div>

        <h1 className='text-foreground mb-3 text-2xl font-bold'>
          Dank voor uw aanmelding
        </h1>
        <p className='text-muted-foreground mb-10 text-base leading-7'>
          Ga verder in de portal om uw gegevens bij te werken en uw laadpaal te
          registreren. Een bevestigingsmail wordt naar uw e-mailadres{' '}
          {loginState.email ?? <span>'uw e-mailadres'</span>}
          <span> gestuurd nadat u uw laadpaal heeft geregistreerd.</span>
        </p>

        <Button
          type='button'
          className='h-11 w-full text-base font-semibold'
          disabled={isSubmitting}
          onClick={() => {
            void handlePortalClick()
          }}
        >
          {isSubmitting ? 'Inloggen...' : 'Naar de portal'}
        </Button>

        <div className='text-muted-foreground mt-6 space-y-1 border-t pt-6 text-center text-sm'>
          <p>
            Helpdesk:{' '}
            <a
              href='tel:0850600641'
              className='hover:text-primary transition-colors'
            >
              085 – 060 0641
            </a>
          </p>
          <p>
            <a
              href='mailto:helpdesk@zeres.nl'
              className='hover:text-primary transition-colors'
            >
              helpdesk@zeres.nl
            </a>
          </p>
          <p>
            <a
              href='https://www.zeres.nl'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:text-primary transition-colors'
            >
              www.zeres.nl
            </a>
          </p>
        </div>
      </div>
    </main>
  )
}
